<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import Button from "primevue/button";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { router } from "@inertiajs/vue3";
import CustomerSelectDialog from "../Components/CustomerSelectDialog.vue";
import ComposeMessageDialog from "../Components/ComposeMessageDialog.vue";
import Dropdown from "primevue/dropdown";

const props = defineProps({
    customers: {
        type: Array,
        default: () => []
    },
    filter: {
        type: String,
        default: 'active'
    },
    success: {
        type: String,
        default: null
    }
});

const toast = useToast();

const customerSearch = ref("");
const showCustomerSelectDialog = ref(false);
const showComposeDialog = ref(false);
const selectedRecipients = ref([]);
const selectedFilter = ref(props.filter);

const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'All Active', value: 'active' }
];

// Check for success message on component mount
onMounted(() => {
    if (props.success) {
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: props.success,
            life: 3000
        });
    }
});

const filteredCustomers = computed(() => {
    if (!customerSearch.value) return props.customers;
    
    return props.customers.filter(customer => 
        customer.name.toLowerCase().includes(customerSearch.value.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerSearch.value.toLowerCase())
    );
});


const handleSendToIndividual = (customer) => {
    selectedRecipients.value = [customer];
    selectedUsingSelectAll.value = false;
    showComposeDialog.value = true;
};

const handleSendToMany = () => {
    showCustomerSelectDialog.value = true;
};

const selectedUsingSelectAll = ref(false);

const handleCustomersSelected = (customers, isSelectAll) => {
    selectedRecipients.value = customers;
    selectedUsingSelectAll.value = isSelectAll;
    showCustomerSelectDialog.value = false;
    showComposeDialog.value = true;
};

const handleMessageSent = () => {
    showComposeDialog.value = false;
    selectedRecipients.value = [];
    selectedUsingSelectAll.value = false;
    // TODO: Refresh history log
};

const handleTabChange = (event) => {
    // If History Log tab is clicked (index 1), redirect to history page
    if (event.index === 1) {
        router.visit('/admin/notifications/history');
    }
};

const handleFilterChange = () => {
    router.get('/admin/notifications', { filter: selectedFilter.value }, { 
        preserveState: true,
        preserveScroll: true
    });
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notifications Dashboard</h1>
                <Button 
                    label="Send to Many" 
                    icon="pi pi-send" 
                    @click="handleSendToMany"
                    severity="primary"
                />
            </div>

            <TabView @tab-change="handleTabChange">
                <TabPanel header="Customers">
                    <div class="mb-4 flex gap-4 items-center">
                        <IconField iconPosition="left" class="w-full max-w-md">
                            <InputIcon class="pi pi-search" />
                            <InputText 
                                v-model="customerSearch" 
                                placeholder="Search customers..." 
                                class="w-full"
                            />
                        </IconField>
                        
                        <Dropdown 
                            v-model="selectedFilter" 
                            :options="filterOptions" 
                            optionLabel="label" 
                            optionValue="value"
                            @change="handleFilterChange"
                            class="w-40"
                        />
                    </div>

                    <DataTable 
                        :value="filteredCustomers" 
                        dataKey="id"
                        :rows="10"
                        :paginator="true"
                        responsiveLayout="scroll"
                        class="mt-4"
                    >
                        <Column field="name" header="Name">
                            <template #body="{ data }">
                                <div class="flex items-center gap-3">
                                    <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                        <i class="pi pi-user text-gray-600"></i>
                                    </div>
                                    <div>
                                        <div class="font-medium">{{ data.name }}</div>
                                        <div class="text-sm text-gray-500">{{ data.email }}</div>
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <Column field="lastActive" header="Last Active"></Column>
                        <Column header="Action" style="width: 100px">
                            <template #body="{ data }">
                                <Button 
                                    label="Send" 
                                    size="small"
                                    @click="handleSendToIndividual(data)"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>

                <TabPanel header="History Log">
                    <!-- This tab will redirect to history page when clicked -->
                </TabPanel>
            </TabView>
        </div>

        <CustomerSelectDialog 
            v-model:visible="showCustomerSelectDialog"
            :customers="customers"
            :currentFilter="selectedFilter"
            @select="handleCustomersSelected"
        />

        <ComposeMessageDialog 
            v-model:visible="showComposeDialog"
            :recipients="selectedRecipients"
            :currentFilter="selectedFilter"
            :totalCustomers="customers.length"
            :sendToAll="selectedUsingSelectAll"
            @sent="handleMessageSent"
        />
    </AdminLayout>
</template>